/**
 * AdminSystemPlatformsPage — admin CRUD for the global
 * `system_platforms/{platformId}` catalogue and nested `formats/*`.
 *
 * Runtime content-plan flows treat this Firestore catalogue as the only
 * product source of truth for platform definitions. Author platform docs are
 * copies/enrichments of these records.
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../contexts/AuthContext';
import { SDialog } from '@modules/ui-components';
import {
  SDataTable,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import { StorageService } from '@modules/firebase-core/services/StorageService';
import { PostTemplateEditDialog } from '@modules/content-plan/components/PostTemplateEditDialog';
import {
  BrandStyleKitEditor,
  BrandStyleKitScopeSelector,
  DESIGN_PROFILE_OPTIONS,
  brandStyleKitDraftToPatch,
  emptyBrandStyleKitDraft,
  inferBrandStyleKitMimeType,
  profileToBrandStyleKitDraft,
  type BrandStyleKitDraft,
} from '@modules/content-plan/components/design/BrandStyleKitEditor';
import {
  systemPlatformService,
  validateSystemPlatformDraft,
  type SystemBrandStyleKitProfile,
  type SystemBrandStyleKitSet,
  type SystemDesignSystemPackage,
  type SystemDesignSystemPackageProfileInput,
  type SystemPlatformDraft,
  type SystemPostFormat,
} from '@modules/content-plan/services/SystemPlatformService';
import type { PostTemplate } from '@modules/content-plan/types';
import { useSystemPlatforms } from '@modules/content-plan/hooks/useSystemPlatforms';
import { PlatformCadenceFields } from '@modules/content-plan/components/platforms/PlatformCadenceFields';
import {
  cadenceFieldSlotsToPlatformOptimalSlots,
  resolvePlatformOptimalFieldSlots,
  type CadenceFieldSlot,
} from '@modules/content-plan/utils/strategyCadence';
import type { PlatformDesignProfile } from '@modules/content-plan/types';

const ADMIN_TESTID = 'admin-system-platforms-page';

type DraftState = {
  platform: SystemPlatformDraft;
  formats: SystemPostFormat[];
  postTemplates: PostTemplate[];
  brandStyleKits: SystemBrandStyleKitSet[];
  designSystemPackages: SystemDesignSystemPackage[];
  optimalSlots: CadenceFieldSlot[];
  defaultFormatIdsRaw: string;
  rewardSignalsRaw: string;
  forbiddenPatternsRaw: string;
};

type FormatDialogState = {
  index: number | null;
  format: SystemPostFormat;
};

type TemplateDialogState = {
  mode: 'create' | 'edit';
  originalId: string | null;
  template: Partial<PostTemplate>;
};

type BrandKitDialogState = {
  mode: 'create' | 'edit';
  originalProfileKey: string | null;
  originalProfileId: string | null;
  profileKey: string;
  profileId: string;
  name: string;
  description: string;
  draft: BrandStyleKitDraft;
  setCurrent: boolean;
};

type FormatTableRow = Record<string, unknown> & {
  id: string;
  name: string;
  formatType: string;
  constraintsPreview: string;
  assetsPreview: string;
  status: string;
  sourceIndex: number;
};

type TemplateTableRow = Record<string, unknown> & {
  id: string;
  name: string;
  goal: string;
  lengthBucket: string;
  platform: string;
  recommendedFormats: string;
  sourceTemplate: PostTemplate;
};

type BrandKitTableRow = Record<string, unknown> & {
  id: string;
  profileKey: string;
  displayName: string;
  currentProfileId: string;
  profilesCount: number;
  sourceSet: SystemBrandStyleKitSet;
};

type DesignPackageTableRow = Record<string, unknown> & {
  id: string;
  name: string;
  status: string;
  coverage: string;
  current: boolean;
  sourcePackage: SystemDesignSystemPackage;
};

function emptyDraft(): DraftState {
  return {
    platform: {
      platformId: '',
      displayName: '',
      icon: '',
      isActive: false,
      maxPostLength: undefined,
      optimalPostFrequency: { min: 1, max: 1 },
      linkPenalty: undefined,
      linkPlacementRule: '',
      contentLifespanHours: undefined,
      defaultFormatIds: [],
      platformSpecificInstructions: '',
      defaultHashtagPolicy: '',
      defaultEmojiPolicy: '',
      rewardSignals: [],
      forbiddenPatterns: [],
    },
    formats: [],
    postTemplates: [],
    brandStyleKits: [],
    designSystemPackages: [],
    optimalSlots: [],
    defaultFormatIdsRaw: '',
    rewardSignalsRaw: '',
    forbiddenPatternsRaw: '',
  };
}

function emptyFormatDraft(): SystemPostFormat {
  return {
    id: '',
    name: '',
    formatType: 'long_text',
    description: '',
    constraints: [],
    assetSlots: [],
    hookInstructions: '',
    formatSpecificInstructions: '',
    isActive: true,
    constraintsRaw: '',
    assetSlotsRaw: '[]',
  };
}

function emptyTemplateDraft(platformId?: string): Partial<PostTemplate> {
  return {
    id: '',
    name: '',
    description: '',
    goal: 'education',
    platform: platformId || 'any',
    tone: 'professional',
    lengthBucket: 'medium',
    scaffold: '',
    variables: [],
    elements: {},
    compatibleFormatTypes: [],
    recommendedFormats: [],
    version: 1,
    scope: 'system' as any,
  };
}

function emptyBrandKitDraft(): BrandKitDialogState {
  return {
    mode: 'create',
    originalProfileKey: null,
    originalProfileId: null,
    profileKey: 'design',
    profileId: 'profile_v1',
    name: '',
    description: '',
    draft: emptyBrandStyleKitDraft(),
    setCurrent: true,
  };
}

function numberOrUndefined(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function toDraft(
  platform: SystemPlatformDraft,
  formats: SystemPostFormat[],
  postTemplates: PostTemplate[] = [],
  brandStyleKits: SystemBrandStyleKitSet[] = [],
  designSystemPackages: SystemDesignSystemPackage[] = [],
): DraftState {
  return {
    platform: {
      ...platform,
      defaultFormatIds: platform.defaultFormatIds ?? [],
    },
    formats: formats.map((format) => ({
      ...format,
      constraintsRaw: (format.constraints ?? []).join('\n'),
      assetSlotsRaw: JSON.stringify(format.assetSlots ?? [], null, 2),
    } as SystemPostFormat)),
    postTemplates,
    brandStyleKits,
    designSystemPackages,
    optimalSlots: resolvePlatformOptimalFieldSlots(platform),
    defaultFormatIdsRaw: (platform.defaultFormatIds ?? []).join(', '),
    rewardSignalsRaw: (platform.rewardSignals ?? []).join('\n'),
    forbiddenPatternsRaw: (platform.forbiddenPatterns ?? []).join('\n'),
  };
}

function postTemplateRecommendedFormatPreview(template: PostTemplate): string {
  const recommended = ((template as any).recommendedFormats ?? [])
    .map((item: any) => item?.formatType)
    .filter(Boolean);
  if (recommended.length > 0) return recommended.join(', ');
  const compatible = ((template as any).compatibleFormatTypes ?? []).filter(Boolean);
  return compatible.length > 0 ? compatible.join(', ') : 'not set';
}

function sortPostTemplates(templates: PostTemplate[]): PostTemplate[] {
  return [...templates].sort((a, b) => String(a.name ?? a.id).localeCompare(String(b.name ?? b.id)));
}

function splitLines(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function parseJsonArray(raw: string | undefined, fieldName: string): Array<Record<string, unknown>> | undefined {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return undefined;
  const parsed = JSON.parse(trimmed);
  if (!Array.isArray(parsed)) throw new Error(`${fieldName} must be a JSON array`);
  return parsed as Array<Record<string, unknown>>;
}

function formatConstraintPreview(format: SystemPostFormat): string {
  const constraints = splitLines(String((format as any).constraintsRaw ?? (format.constraints ?? []).join('\n')));
  if (constraints.length === 0) return 'No constraints';
  if (constraints.length === 1) return constraints[0];
  return `${constraints.length} constraints`;
}

function formatAssetPreview(format: SystemPostFormat): string {
  const raw = String((format as any).assetSlotsRaw ?? '').trim();
  if (!raw || raw === '[]') return 'No asset slots';
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? `${parsed.length} asset slot${parsed.length === 1 ? '' : 's'}` : 'Invalid JSON';
  } catch {
    return 'Invalid JSON';
  }
}

export default function AdminSystemPlatformsPage(): React.ReactElement {
  const { isAdmin, adminLoaded } = useAuth();
  const { platforms, loading, error } = useSystemPlatforms();
  const [selectedPlatformId, setSelectedPlatformId] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<DraftState>(() => emptyDraft());
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{ severity: 'success' | 'warning' | 'error'; message: string } | null>(null);
  const [formatDialog, setFormatDialog] = React.useState<FormatDialogState | null>(null);
  const [templateDialog, setTemplateDialog] = React.useState<TemplateDialogState | null>(null);
  const [brandKitDialog, setBrandKitDialog] = React.useState<BrandKitDialogState | null>(null);
  const [uploadingBrandKitRefIndex, setUploadingBrandKitRefIndex] = React.useState<number | null>(null);

  const selectedExists = Boolean(selectedPlatformId);
  const currentDesignPackage = React.useMemo(
    () => draft.designSystemPackages.find((item) => item.current) ?? draft.designSystemPackages[0] ?? null,
    [draft.designSystemPackages],
  );

  React.useEffect(() => {
    if (!selectedPlatformId) return;
    let cancelled = false;
    setLoadingDetails(true);
    setFeedback(null);
    systemPlatformService.getPlatform(selectedPlatformId)
      .then((loaded) => {
        if (cancelled || !loaded) return;
        setDraft(toDraft(
          loaded,
          loaded.formats,
          loaded.postTemplates ?? [],
          loaded.brandStyleKits ?? [],
          loaded.designSystemPackages ?? [],
        ));
        if (loaded.catalogWarnings?.length) {
          setFeedback({ severity: 'warning', message: loaded.catalogWarnings.join('; ') });
        }
      })
      .catch((e: any) => {
        if (!cancelled) setFeedback({ severity: 'error', message: e?.message ?? 'Failed to load platform' });
      })
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPlatformId]);

  const patchPlatform = (patch: Partial<SystemPlatformDraft>) => {
    setDraft((current) => ({
      ...current,
      platform: { ...current.platform, ...patch },
    }));
  };

  const patchFormatDialog = (patch: Partial<SystemPostFormat>) => {
    setFormatDialog((current) => (
      current
        ? { ...current, format: { ...current.format, ...patch } }
        : current
    ));
  };

  const patchBrandKitDialog = (patch: Partial<BrandKitDialogState>) => {
    setBrandKitDialog((current) => (current ? { ...current, ...patch } : current));
  };

  const setBrandKitDraft = (nextDraft: BrandStyleKitDraft) => {
    setBrandKitDialog((current) => (
      current ? { ...current, draft: nextDraft } : current
    ));
  };

  const patchBrandKitReference = (index: number, patch: Partial<BrandStyleKitDraft['references'][number]>) => {
    setBrandKitDialog((current) => (
      current
        ? {
          ...current,
          draft: {
            ...current.draft,
            references: current.draft.references.map((ref, refIndex) => (
              refIndex === index ? { ...ref, ...patch } : ref
            )),
          },
        }
        : current
    ));
  };

  const removeBrandKitReference = (index: number) => {
    setBrandKitDialog((current) => (
      current
        ? {
          ...current,
          draft: {
            ...current.draft,
            references: current.draft.references.filter((_, refIndex) => refIndex !== index),
          },
        }
        : current
    ));
  };

  const uploadBrandKitReferenceFile = async (
    profileKey: string,
    file: File,
  ): Promise<{ url: string; mimeType: string }> => {
    const platformId = String(draft.platform.platformId || selectedPlatformId || '').trim();
    if (!platformId) throw new Error('Select a platform before uploading references.');
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const packageId = currentDesignPackage?.packageId || 'default-package-v1';
    const path = `system_platforms/${platformId}/design_system_packages/${packageId}/profiles/${profileKey}/references/${Date.now()}_${sanitizedName}`;
    const result = await new StorageService().uploadImage(path, file);
    return { url: result.assetUrl.origin, mimeType: file.type || inferBrandStyleKitMimeType(result.assetUrl.origin) };
  };

  const handleBrandKitReferenceMediaChange = async (index: number, value: string | File | null) => {
    if (!brandKitDialog) return;
    setFeedback(null);
    if (value instanceof File) {
      setUploadingBrandKitRefIndex(index);
      try {
        const uploaded = await uploadBrandKitReferenceFile(
          brandKitDialog.profileKey.trim() || 'design',
          value,
        );
        patchBrandKitReference(index, uploaded);
      } catch (e) {
        setFeedback({ severity: 'error', message: (e as Error).message });
      } finally {
        setUploadingBrandKitRefIndex(null);
      }
      return;
    }
    if (typeof value === 'string') {
      const url = value.trim();
      patchBrandKitReference(index, {
        url,
        mimeType: url ? inferBrandStyleKitMimeType(url) : 'image/png',
      });
      return;
    }
    removeBrandKitReference(index);
  };

  const addFormat = () => {
    setFormatDialog({ index: null, format: emptyFormatDraft() });
  };

  const openFormatDialog = (index: number) => {
    const format = draft.formats[index];
    setFormatDialog({
      index,
      format: {
        ...format,
        constraintsRaw: (format as any).constraintsRaw ?? (format.constraints ?? []).join('\n'),
        assetSlotsRaw: (format as any).assetSlotsRaw ?? JSON.stringify(format.assetSlots ?? [], null, 2),
      },
    });
  };

  const applyFormatDialog = () => {
    if (!formatDialog) return;
    const nextFormat = {
      ...formatDialog.format,
      id: String(formatDialog.format.id ?? '').trim(),
      name: String(formatDialog.format.name ?? '').trim(),
      formatType: String(formatDialog.format.formatType ?? '').trim(),
      description: String(formatDialog.format.description ?? '').trim(),
    };
    setDraft((current) => {
      if (formatDialog.index === null) {
        return { ...current, formats: [...current.formats, nextFormat] };
      }
      return {
        ...current,
        formats: current.formats.map((format, index) => (
          index === formatDialog.index ? nextFormat : format
        )),
      };
    });
    setFormatDialog(null);
  };

  const removeFormat = (index: number) => {
    setDraft((current) => ({
      ...current,
      formats: current.formats.filter((_, i) => i !== index),
    }));
  };

  const addTemplate = () => {
    const platformId = String(draft.platform.platformId || selectedPlatformId || '').trim();
    setTemplateDialog({
      mode: 'create',
      originalId: null,
      template: emptyTemplateDraft(platformId),
    });
  };

  const openTemplateDialog = (template: PostTemplate) => {
    setTemplateDialog({
      mode: 'edit',
      originalId: template.id,
      template,
    });
  };

  const addBrandKitProfile = () => {
    setBrandKitDialog(emptyBrandKitDraft());
  };

  const openBrandKitProfile = (set: SystemBrandStyleKitSet, profile?: SystemBrandStyleKitProfile) => {
    const selected = profile ?? set.profiles?.find((item) => item.id === set.currentProfileId) ?? set.profiles?.[0];
    if (!selected) return;
    setBrandKitDialog({
      mode: 'edit',
      originalProfileKey: set.profileKey,
      originalProfileId: selected.id,
      profileKey: set.profileKey,
      profileId: selected.id,
      name: selected.name ?? selected.id,
      description: selected.description ?? set.description ?? '',
      draft: profileToBrandStyleKitDraft(selected),
      setCurrent: set.currentProfileId === selected.id,
    });
  };

  const saveBrandKitDialog = async () => {
    if (!brandKitDialog || !selectedPlatformId) return;
    setSaving(true);
    setFeedback(null);
    try {
      const profileKey = brandKitDialog.profileKey.trim();
      const packageId = currentDesignPackage?.packageId || 'default-package-v1';
      const patch = brandStyleKitDraftToPatch(selectedPlatformId, brandKitDialog.draft);
      const nextProfilesByKey = new Map<string, SystemDesignSystemPackageProfileInput>();
      const packageProfiles = currentDesignPackage?.profiles?.length
        ? currentDesignPackage.profiles
        : draft.brandStyleKits
          .map((set) => {
            const setProfileKey = String(set.profileKey);
            const currentProfileId = String(set.currentProfileId ?? '').trim();
            const setProfile = set.profiles?.find((profile) => profile.id === currentProfileId) ?? set.profiles?.[0];
            return setProfile
              ? ({
                ...setProfile,
                id: setProfileKey,
                packageId,
                profileKey: setProfileKey,
                profileId: packageId,
                source: 'package_profile',
              } as SystemDesignSystemPackageProfileInput)
              : null;
          })
          .filter((profile): profile is SystemDesignSystemPackageProfileInput => Boolean(profile));
      packageProfiles.forEach((profile) => nextProfilesByKey.set(String(profile.profileKey), {
        ...profile,
        id: String(profile.profileKey),
        packageId,
        profileId: packageId,
      }));
      nextProfilesByKey.set(profileKey, {
        ...(patch as PlatformDesignProfile),
        id: profileKey,
        packageId,
        profileId: packageId,
        profileKey,
        name: brandKitDialog.name.trim() || `${profileKey} package profile`,
        description: brandKitDialog.description.trim() || undefined,
        platformId: selectedPlatformId,
        source: 'package_profile',
        isComplete: true,
      } as SystemDesignSystemPackageProfileInput);

      await systemPlatformService.saveDesignSystemPackage({
        platformId: selectedPlatformId,
        designPackage: {
          packageId,
          name: currentDesignPackage?.name ?? `${selectedPlatformId} design package`,
          description: currentDesignPackage?.description ?? 'System platform design package.',
          status: currentDesignPackage?.status ?? 'draft',
          visualFoundation: currentDesignPackage?.visualFoundation,
          contentHash: currentDesignPackage?.contentHash,
          targetProfileKeys: DESIGN_PROFILE_OPTIONS.map((option) => option.value),
          profiles: Array.from(nextProfilesByKey.values()),
        },
        setCurrent: brandKitDialog.setCurrent,
      });
      const loaded = await systemPlatformService.getPlatform(selectedPlatformId);
      if (loaded) {
        setDraft(toDraft(
          loaded,
          loaded.formats,
          loaded.postTemplates ?? [],
          loaded.brandStyleKits ?? [],
          loaded.designSystemPackages ?? [],
        ));
      }
      setBrandKitDialog(null);
      setFeedback({ severity: 'success', message: 'Brand style profile saved.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const makeDesignPackageCurrent = async (packageId: string) => {
    if (!selectedPlatformId) return;
    setSaving(true);
    setFeedback(null);
    try {
      await systemPlatformService.makeDesignSystemPackageCurrent(selectedPlatformId, packageId);
      const loaded = await systemPlatformService.getPlatform(selectedPlatformId);
      if (loaded) {
        setDraft(toDraft(
          loaded,
          loaded.formats,
          loaded.postTemplates ?? [],
          loaded.brandStyleKits ?? [],
          loaded.designSystemPackages ?? [],
        ));
      }
      setFeedback({ severity: 'success', message: 'Design package made current.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const saveTemplateDialog = async (draftTemplate: Partial<PostTemplate>) => {
    if (!templateDialog || !selectedPlatformId) return;
    setSaving(true);
    setFeedback(null);
    try {
      const templateId = String(draftTemplate.id ?? templateDialog.originalId ?? '').trim();
      if (!templateId) throw new Error('Template ID is required');
      const payload = { ...(draftTemplate as Record<string, unknown>) };
      delete payload.id;
      if (!String(payload.scaffold ?? '').trim()) {
        throw new Error('Template scaffold is required');
      }
      const version = Number(payload.version ?? 1);
      payload.version = Number.isFinite(version) ? version : 1;
      payload.scope = String(payload.scope ?? 'system');
      payload.platform = String(payload.platform ?? selectedPlatformId);
      payload.systemPlatformId = String(payload.systemPlatformId ?? selectedPlatformId);

      const nextTemplate = { ...payload, id: templateId } as PostTemplate & Record<string, unknown>;
      await systemPlatformService.savePostTemplate(
        selectedPlatformId,
        nextTemplate,
        templateDialog.originalId,
      );

      setDraft((current) => ({
        ...current,
        postTemplates: sortPostTemplates([
          ...current.postTemplates.filter((template) => template.id !== templateDialog.originalId && template.id !== templateId),
          nextTemplate,
        ]),
      }));
      setTemplateDialog(null);
      setFeedback({ severity: 'success', message: 'Post template saved.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    if (!selectedPlatformId) return;
    const confirmed = window.confirm(`Delete post template "${templateId}" from ${selectedPlatformId}?`);
    if (!confirmed) return;
    setSaving(true);
    setFeedback(null);
    try {
      await systemPlatformService.deletePostTemplate(selectedPlatformId, templateId);
      setDraft((current) => ({
        ...current,
        postTemplates: current.postTemplates.filter((template) => template.id !== templateId),
      }));
      if (templateDialog?.originalId === templateId) setTemplateDialog(null);
      setFeedback({ severity: 'success', message: 'Post template deleted.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const newPlatform = () => {
    setSelectedPlatformId(null);
    setDraft(emptyDraft());
    setFeedback(null);
  };

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const defaultFormatIds = draft.defaultFormatIdsRaw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      const platform: SystemPlatformDraft = {
        ...draft.platform,
        platformId: String(draft.platform.platformId ?? '').trim(),
        displayName: String(draft.platform.displayName ?? '').trim(),
        icon: String(draft.platform.icon ?? '').trim() || undefined,
        linkPlacementRule: String(draft.platform.linkPlacementRule ?? '').trim() || undefined,
        platformSpecificInstructions: String(draft.platform.platformSpecificInstructions ?? '').trim() || undefined,
        defaultHashtagPolicy: String(draft.platform.defaultHashtagPolicy ?? '').trim() || undefined,
        defaultEmojiPolicy: String(draft.platform.defaultEmojiPolicy ?? '').trim() || undefined,
        rewardSignals: splitLines(draft.rewardSignalsRaw),
        forbiddenPatterns: splitLines(draft.forbiddenPatternsRaw),
        defaultFormatIds,
        optimalSlots: draft.optimalSlots.length > 0
          ? cadenceFieldSlotsToPlatformOptimalSlots(draft.optimalSlots)
          : undefined,
      };
      delete (platform as any).promptRef;
      delete (platform as any).optimalTimes;
      const formats = draft.formats.map((format) => ({
        ...format,
        constraints: splitLines(String((format as any).constraintsRaw ?? (format.constraints ?? []).join('\n'))),
        assetSlots: parseJsonArray(String((format as any).assetSlotsRaw ?? JSON.stringify(format.assetSlots ?? [], null, 2)), 'assetSlots'),
        hookInstructions: String(format.hookInstructions ?? '').trim() || undefined,
        formatSpecificInstructions: String(format.formatSpecificInstructions ?? '').trim() || undefined,
      }));
      for (const format of formats) {
        delete (format as any).constraintsRaw;
        delete (format as any).assetSlotsRaw;
      }
      const validation = validateSystemPlatformDraft(platform, formats);
      if (validation.length > 0) throw new Error(validation.join('; '));
      await systemPlatformService.replacePlatformWithFormats(platform, formats);
      setSelectedPlatformId(platform.platformId);
      setFeedback({ severity: 'success', message: 'System platform saved.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const deleteSelected = async () => {
    if (!selectedPlatformId) return;
    const confirmed = window.confirm(`Delete system platform "${selectedPlatformId}" and all nested formats?`);
    if (!confirmed) return;
    setSaving(true);
    setFeedback(null);
    try {
      await systemPlatformService.deletePlatform(selectedPlatformId);
      newPlatform();
      setFeedback({ severity: 'success', message: 'System platform deleted.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const templateRows = React.useMemo<TemplateTableRow[]>(() => (
    draft.postTemplates.map((template) => ({
      id: template.id,
      name: template.name || 'Untitled template',
      goal: template.goal || 'not set',
      lengthBucket: template.lengthBucket || 'not set',
      platform: template.platform || 'any',
      recommendedFormats: postTemplateRecommendedFormatPreview(template),
      sourceTemplate: template,
    }))
  ), [draft.postTemplates]);

  const designPackageRows = React.useMemo<DesignPackageTableRow[]>(() => (
    draft.designSystemPackages.map((designPackage) => ({
      id: designPackage.packageId,
      name: designPackage.name || designPackage.packageId,
      status: designPackage.status || 'draft',
      coverage: `${designPackage.profiles?.length ?? designPackage.profileCount ?? 0}/${DESIGN_PROFILE_OPTIONS.length}`,
      current: designPackage.current === true,
      sourcePackage: designPackage,
    }))
  ), [draft.designSystemPackages]);

  const brandKitRows = React.useMemo<BrandKitTableRow[]>(() => (
    draft.brandStyleKits.map((set) => ({
      id: set.profileKey,
      profileKey: set.profileKey,
      displayName: set.displayName || set.profileKey,
      currentProfileId: set.currentProfileId || 'not set',
      profilesCount: set.profiles?.length ?? 0,
      sourceSet: set,
    }))
  ), [draft.brandStyleKits]);

  const templateColumns = React.useMemo<SDataTableColumnDef<TemplateTableRow>[]>(() => [
    {
      accessorKey: 'id',
      header: 'Template ID',
      Cell: ({ cell }: any) => (
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
          {String(cell.getValue() ?? '')}
        </Typography>
      ),
    },
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'goal', header: 'Goal' },
    { accessorKey: 'lengthBucket', header: 'Length' },
    { accessorKey: 'platform', header: 'Source' },
    { accessorKey: 'recommendedFormats', header: 'Recommended formats' },
  ], []);

  const brandKitColumns = React.useMemo<SDataTableColumnDef<BrandKitTableRow>[]>(() => [
    {
      accessorKey: 'profileKey',
      header: 'Profile key',
      Cell: ({ cell }: any) => (
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
          {String(cell.getValue() ?? '')}
        </Typography>
      ),
    },
    { accessorKey: 'displayName', header: 'Name' },
    { accessorKey: 'currentProfileId', header: 'Current profile' },
    { accessorKey: 'profilesCount', header: 'Variants' },
  ], []);

  const designPackageColumns = React.useMemo<SDataTableColumnDef<DesignPackageTableRow>[]>(() => [
    {
      accessorKey: 'id',
      header: 'Package ID',
      Cell: ({ cell }: any) => (
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
          {String(cell.getValue() ?? '')}
        </Typography>
      ),
    },
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'status', header: 'Status' },
    { accessorKey: 'coverage', header: 'Coverage' },
    {
      accessorKey: 'current',
      header: 'Current',
      Cell: ({ cell }: any) => (
        cell.getValue()
          ? <Chip size="small" color="success" label="Current" />
          : <Typography variant="caption" color="text.secondary">Available</Typography>
      ),
    },
  ], []);

  const formatRows = React.useMemo<FormatTableRow[]>(() => (
    draft.formats.map((format, index) => ({
      id: format.id || 'new_format',
      name: format.name || 'Untitled format',
      formatType: format.formatType || 'not set',
      constraintsPreview: formatConstraintPreview(format),
      assetsPreview: formatAssetPreview(format),
      status: format.isActive === false ? 'Inactive' : 'Active',
      sourceIndex: index,
    }))
  ), [draft.formats]);

  const formatColumns = React.useMemo<SDataTableColumnDef<FormatTableRow>[]>(() => [
    {
      accessorKey: 'id',
      header: 'Format ID',
      Cell: ({ cell }: any) => (
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
          {String(cell.getValue() ?? '')}
        </Typography>
      ),
    },
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'formatType', header: 'Type' },
    { accessorKey: 'constraintsPreview', header: 'Constraints' },
    { accessorKey: 'assetsPreview', header: 'Assets' },
    { accessorKey: 'status', header: 'Status' },
  ], []);

  if (!adminLoaded) {
    return (
      <Box sx={{ p: 3 }} data-testid={`${ADMIN_TESTID}-loading`}>
        <Alert severity="info">Checking admin access...</Alert>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }} data-testid={`${ADMIN_TESTID}-no-access`}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  return (
    <>
    <Box sx={{ p: 3 }} data-testid={ADMIN_TESTID}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5">System Platforms</Typography>
          <Typography variant="body2" color="text.secondary">
            Global source of truth under <code>system_platforms/{'{platformId}'}</code>.
            Active platforms must have at least one nested <code>formats</code> document.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error.message}</Alert>}
        {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5 }}>
              <Typography variant="subtitle2">Catalog</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={newPlatform}>
                New
              </Button>
            </Stack>
            <Divider />
            {loading ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Loading platforms...
              </Typography>
            ) : platforms.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No system platforms yet.
              </Typography>
            ) : (
              <List dense disablePadding>
                {platforms.map((platform) => (
                  <ListItemButton
                    key={platform.platformId}
                    selected={platform.platformId === selectedPlatformId}
                    onClick={() => setSelectedPlatformId(platform.platformId)}
                  >
                    <ListItemText
                      primary={platform.displayName}
                      secondary={`${platform.platformId}${platform.isActive === false ? ' · inactive' : ''}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">
                    {selectedExists ? draft.platform.displayName || selectedPlatformId : 'New platform'}
                  </Typography>
                  {loadingDetails && (
                    <Typography variant="caption" color="text.secondary">Loading details...</Typography>
                  )}
                  {selectedExists && !loadingDetails && (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                      <Chip size="small" variant="outlined" label={`${draft.formats.length} formats`} />
                      <Chip
                        size="small"
                        variant="outlined"
                        color={draft.postTemplates.length > 0 ? 'primary' : 'warning'}
                        label={`${draft.postTemplates.length} templates`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        color={draft.brandStyleKits.length > 0 ? 'primary' : 'default'}
                        label={`${draft.brandStyleKits.length} style kits`}
                      />
                    </Stack>
                  )}
                </Box>
                {selectedExists && (
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Delete platform"
                    onClick={deleteSelected}
                    disabled={saving}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={save}
                  disabled={saving || loadingDetails}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Stack>

              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  size="small"
                  label="Platform ID"
                  value={draft.platform.platformId}
                  onChange={(e) => patchPlatform({ platformId: e.target.value })}
                  disabled={selectedExists}
                  helperText="Firestore doc id. Lowercase letters, numbers, underscores, hyphens."
                />
                <TextField
                  size="small"
                  label="Display name"
                  value={draft.platform.displayName}
                  onChange={(e) => patchPlatform({ displayName: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Icon"
                  value={draft.platform.icon ?? ''}
                  onChange={(e) => patchPlatform({ icon: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Max post length"
                  type="number"
                  value={draft.platform.maxPostLength ?? ''}
                  onChange={(e) => patchPlatform({ maxPostLength: numberOrUndefined(e.target.value) })}
                />
                <TextField
                  size="small"
                  label="Content lifespan hours"
                  type="number"
                  value={draft.platform.contentLifespanHours ?? ''}
                  onChange={(e) => patchPlatform({ contentLifespanHours: numberOrUndefined(e.target.value) })}
                />
                <TextField
                  size="small"
                  label="Frequency min"
                  type="number"
                  value={draft.platform.optimalPostFrequency?.min ?? ''}
                  onChange={(e) => patchPlatform({
                    optimalPostFrequency: {
                      ...(draft.platform.optimalPostFrequency ?? {}),
                      min: numberOrUndefined(e.target.value),
                    },
                  })}
                />
                <TextField
                  size="small"
                  label="Frequency max"
                  type="number"
                  value={draft.platform.optimalPostFrequency?.max ?? ''}
                  onChange={(e) => patchPlatform({
                    optimalPostFrequency: {
                      ...(draft.platform.optimalPostFrequency ?? {}),
                      max: numberOrUndefined(e.target.value),
                    },
                  })}
                />
                <TextField
                  size="small"
                  label="Link penalty"
                  value={draft.platform.linkPenalty ?? ''}
                  onChange={(e) => patchPlatform({ linkPenalty: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Link placement rule"
                  value={draft.platform.linkPlacementRule ?? ''}
                  onChange={(e) => patchPlatform({ linkPlacementRule: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.platform.isActive !== false}
                      onChange={(e) => patchPlatform({ isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Box>

              <TextField
                label="Platform-specific instructions"
                value={draft.platform.platformSpecificInstructions ?? ''}
                onChange={(e) => patchPlatform({ platformSpecificInstructions: e.target.value })}
                multiline
                minRows={4}
                helperText="Channel mechanics and constraints only. Do not put author tone of voice here."
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="Default hashtag policy"
                  size="small"
                  value={draft.platform.defaultHashtagPolicy ?? ''}
                  onChange={(e) => patchPlatform({ defaultHashtagPolicy: e.target.value })}
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Default emoji policy"
                  size="small"
                  value={draft.platform.defaultEmojiPolicy ?? ''}
                  onChange={(e) => patchPlatform({ defaultEmojiPolicy: e.target.value })}
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Reward signals"
                  size="small"
                  value={draft.rewardSignalsRaw}
                  onChange={(e) => setDraft((current) => ({ ...current, rewardSignalsRaw: e.target.value }))}
                  multiline
                  minRows={4}
                  helperText="One signal per line."
                />
                <TextField
                  label="Forbidden patterns"
                  size="small"
                  value={draft.forbiddenPatternsRaw}
                  onChange={(e) => setDraft((current) => ({ ...current, forbiddenPatternsRaw: e.target.value }))}
                  multiline
                  minRows={4}
                  helperText="One pattern per line."
                />
              </Box>

              <TextField
                label="Default format IDs"
                value={draft.defaultFormatIdsRaw}
                onChange={(e) => setDraft((current) => ({ ...current, defaultFormatIdsRaw: e.target.value }))}
                size="small"
                helperText="Comma-separated format ids."
              />

              <PlatformCadenceFields
                mode="author"
                slots={draft.optimalSlots}
                onSlotsChange={(next) => setDraft((current) => ({ ...current, optimalSlots: next }))}
                emptyText="No platform-level posting slots configured."
                testIdPrefix={ADMIN_TESTID}
              />

              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Design system packages</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Source package under <code>system_platforms/{'{platformId}'}/design_system_packages</code>. Current package is projected into brand_style_kit.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {draft.designSystemPackages.length} packages
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addBrandKitProfile}
                    disabled={!selectedPlatformId}
                  >
                    Add package slot
                  </Button>
                </Stack>
              </Stack>

              <Box>
                {designPackageRows.length > 0 ? (
                  <SDataTable<DesignPackageTableRow>
                    columns={designPackageColumns}
                    data={designPackageRows}
                    getRowId={(row) => row.id}
                    enableSorting
                    enableRowActions
                    enableTopToolbar={false}
                    enablePagination={false}
                    renderRowActions={({ row }) => (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {!row.original.current && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckIcon />}
                            onClick={() => { void makeDesignPackageCurrent(row.original.id); }}
                            disabled={saving}
                          >
                            Make current
                          </Button>
                        )}
                      </Stack>
                    )}
                  />
                ) : (
                  <Alert severity="info">
                    No design package exists yet. Add a package slot to create the first platform-level package.
                  </Alert>
                )}
              </Box>

              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Brand style kit projections</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Compatibility projection under <code>system_platforms/{'{platformId}'}/brand_style_kit</code>.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {draft.brandStyleKits.length} profile scopes
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addBrandKitProfile}
                    disabled={!selectedPlatformId}
                  >
                    Add package slot
                  </Button>
                </Stack>
              </Stack>

              <Box>
                {draft.brandStyleKits.length > 0 ? (
                  <SDataTable<BrandKitTableRow>
                    columns={brandKitColumns}
                    data={brandKitRows}
                    getRowId={(row) => row.id}
                    enableSorting
                    enableRowActions
                    enableTopToolbar={false}
                    enablePagination={false}
                    muiTableBodyRowProps={({ row }) => ({
                      hover: true,
                      onClick: () => openBrandKitProfile(row.original.sourceSet),
                      sx: { cursor: 'pointer' },
                    })}
                    renderRowActions={({ row }) => (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          aria-label={`View or edit style kit ${row.original.profileKey}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            openBrandKitProfile(row.original.sourceSet);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  />
                ) : (
                  <Alert severity="info">
                    No brand style kits yet. Add one or run the platform seed to create LinkedIn defaults.
                  </Alert>
                )}
              </Box>

              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Post templates</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Nested catalogue under <code>system_platforms/{'{platformId}'}/post_templates</code>.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {draft.postTemplates.length} templates
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addTemplate}
                    disabled={!selectedPlatformId}
                  >
                    Add template
                  </Button>
                </Stack>
              </Stack>

              <Box>
                {draft.postTemplates.length > 0 ? (
                  <SDataTable<TemplateTableRow>
                    columns={templateColumns}
                    data={templateRows}
                    getRowId={(row) => row.id}
                    enableSorting
                    enableSearch
                    searchInColumn="name"
                    searchMode="global"
                    searchPlaceholder="Search templates..."
                    enableRowActions
                    enableTopToolbar={false}
                    enablePagination={false}
                    muiTableContainerProps={{ sx: { maxHeight: 360, overflow: 'auto' } }}
                    muiTableBodyRowProps={({ row }) => ({
                      hover: true,
                      onClick: () => openTemplateDialog(row.original.sourceTemplate),
                      sx: { cursor: 'pointer' },
                    })}
                    renderRowActions={({ row }) => (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          aria-label={`View or edit template ${row.original.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            openTemplateDialog(row.original.sourceTemplate);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Remove template ${row.original.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void deleteTemplate(row.original.id);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  />
                ) : (
                  <Alert severity="warning">
                    No post templates are nested under this system platform. Run the system post-template seed before importing templates for authors.
                  </Alert>
                )}
              </Box>

              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Post formats</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active platforms cannot be saved without at least one format.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addFormat}>
                  Add format
                </Button>
              </Stack>

              <Box>
                {draft.formats.length > 0 && (
                  <SDataTable<FormatTableRow>
                    columns={formatColumns}
                    data={formatRows}
                    getRowId={(row) => `${row.id}-${row.sourceIndex}`}
                    enableSorting
                    enableRowActions
                    enableTopToolbar={false}
                    enablePagination={false}
                    muiTableBodyRowProps={({ row }) => ({
                      hover: true,
                      onClick: () => openFormatDialog(row.original.sourceIndex),
                      sx: { cursor: 'pointer' },
                    })}
                    renderRowActions={({ row }) => (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          aria-label={`View or edit format ${row.original.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            openFormatDialog(row.original.sourceIndex);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Remove format ${row.original.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            removeFormat(row.original.sourceIndex);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  />
                )}
                {draft.formats.length === 0 && (
                  <Alert severity={draft.platform.isActive === false ? 'info' : 'warning'}>
                    Add at least one post format before activating this platform.
                  </Alert>
                )}
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Box>

    <SDialog
      open={Boolean(formatDialog)}
      onClose={() => setFormatDialog(null)}
      title={formatDialog?.index === null ? 'Add Post Format' : 'Edit Post Format'}
      maxWidth="md"
      fullWidth
      primaryAction={{
        label: 'Apply',
        onClick: applyFormatDialog,
        startIcon: <SaveIcon />,
      }}
    >
      {formatDialog && (
        <Box sx={{ px: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                size="small"
                label="Format ID"
                value={formatDialog.format.id}
                onChange={(e) => patchFormatDialog({ id: e.target.value })}
                helperText="Nested Firestore doc id."
              />
              <TextField
                size="small"
                label="Name"
                value={formatDialog.format.name ?? ''}
                onChange={(e) => patchFormatDialog({ name: e.target.value })}
              />
              <TextField
                size="small"
                label="Format type"
                value={formatDialog.format.formatType ?? ''}
                onChange={(e) => patchFormatDialog({ formatType: e.target.value })}
                helperText="Planner compatibility key, for example long_text, carousel, reel."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formatDialog.format.isActive !== false}
                    onChange={(e) => patchFormatDialog({ isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
            <TextField
              size="small"
              label="Description"
              value={formatDialog.format.description ?? ''}
              onChange={(e) => patchFormatDialog({ description: e.target.value })}
              multiline
              minRows={2}
            />
            <TextField
              size="small"
              label="Constraints"
              value={(formatDialog.format as any).constraintsRaw ?? (formatDialog.format.constraints ?? []).join('\n')}
              onChange={(e) => patchFormatDialog({ constraintsRaw: e.target.value } as any)}
              multiline
              minRows={4}
              helperText="One constraint per line."
            />
            <TextField
              size="small"
              label="Asset slots"
              value={(formatDialog.format as any).assetSlotsRaw ?? JSON.stringify(formatDialog.format.assetSlots ?? [], null, 2)}
              onChange={(e) => patchFormatDialog({ assetSlotsRaw: e.target.value } as any)}
              multiline
              minRows={6}
              helperText={'JSON array of slot descriptors. Sizing per slot: "aspectRatio" (ratio hint, e.g. "1:1") and/or "pixelSize": {"width", "height"} — slots with pixelSize become named container presets in the article screen editor.'}
            />
            <TextField
              size="small"
              label="Hook instructions"
              value={formatDialog.format.hookInstructions ?? ''}
              onChange={(e) => patchFormatDialog({ hookInstructions: e.target.value })}
              multiline
              minRows={3}
            />
            <TextField
              size="small"
              label="Format-specific instructions"
              value={formatDialog.format.formatSpecificInstructions ?? ''}
              onChange={(e) => patchFormatDialog({ formatSpecificInstructions: e.target.value })}
              multiline
              minRows={4}
            />
          </Stack>
        </Box>
      )}
    </SDialog>

    {templateDialog && (
      <PostTemplateEditDialog
        open={Boolean(templateDialog)}
        onClose={() => setTemplateDialog(null)}
        template={templateDialog.template}
        mode={templateDialog.mode}
        title={templateDialog.mode === 'create' ? 'Add Post Template' : 'Edit Post Template'}
        onSave={saveTemplateDialog}
        showTemplateId
        allowTemplateIdEdit
      />
    )}

    <SDialog
      open={Boolean(brandKitDialog)}
      onClose={() => setBrandKitDialog(null)}
      title={brandKitDialog?.mode === 'create' ? 'Add Design Package Slot' : 'Edit Design Package Slot'}
      maxWidth="lg"
      fullWidth
      primaryAction={{
        label: 'Save package slot',
        onClick: saveBrandKitDialog,
        startIcon: <SaveIcon />,
        loading: saving,
        disabled: uploadingBrandKitRefIndex !== null,
      }}
    >
      {brandKitDialog && (
        <Box sx={{ px: 2 }}>
          <Stack spacing={2.5}>
            <BrandStyleKitScopeSelector
              value={brandKitDialog.profileKey}
              onChange={(profileKey) => patchBrandKitDialog({ profileKey })}
              disabled={saving}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                size="small"
                label="Package ID"
                value={currentDesignPackage?.packageId ?? 'default-package-v1'}
                helperText="Current package id. Slot saves into this package."
                disabled
              />
              <TextField
                size="small"
                label="Slot name"
                value={brandKitDialog.name}
                onChange={(e) => patchBrandKitDialog({ name: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={brandKitDialog.setCurrent}
                    onChange={(e) => patchBrandKitDialog({ setCurrent: e.target.checked })}
                  />
                }
                label="Use as current"
              />
            </Box>
            <TextField
              size="small"
              label="Description"
              value={brandKitDialog.description}
              onChange={(e) => patchBrandKitDialog({ description: e.target.value })}
              multiline
              minRows={2}
            />
            <BrandStyleKitEditor
              draft={brandKitDialog.draft}
              onChange={setBrandKitDraft}
              profileKey={brandKitDialog.profileKey}
              platformId={selectedPlatformId ?? draft.platform.platformId ?? 'platform'}
              disabled={saving}
              uploadingReferenceIndex={uploadingBrandKitRefIndex}
              onReferenceMediaChange={handleBrandKitReferenceMediaChange}
              testIdPrefix={`admin-brand-kit-${brandKitDialog.profileKey}`}
            />
          </Stack>
        </Box>
      )}
    </SDialog>
    </>
  );
}
